import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { varAlpha } from 'src/theme/styles';

import { Iconify } from '../../iconify';
import { uploadClasses } from '../classes';

import type { SingleFilePreviewProps } from '../types';

// ----------------------------------------------------------------------

export function SingleFilePreview({ file, sx, className, ...other }: SingleFilePreviewProps) {
  const [textContent, setTextContent] = useState<string>('');
  const [isLoadingText, setIsLoadingText] = useState(false);

  const fileName = typeof file === 'string' ? file : file.name;

  const isFile = file instanceof File;
  const previewUrl = isFile ? URL.createObjectURL(file) : file;

  let type: string;
  if (isFile) {
    type = file.type.split('/')[0];
  } else {
    // Get file type from string
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    switch (fileExtension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        type = 'image';
        break;
      case 'mp4':
      case 'webm':
      case 'ogg':
      case 'mov':
        type = 'video';
        break;
      case 'vtt':
      case 'srt':
      case 'txt':
        type = 'text';
        break;
      default:
        type = 'unknown';
    }
  }

  // Read text content for text files
  useEffect(() => {
    if (type === 'text' && isFile) {
      setIsLoadingText(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setTextContent(content || '');
        setIsLoadingText(false);
      };
      reader.onerror = () => {
        console.error('Failed to read file content');
        setTextContent('');
        setIsLoadingText(false);
      };
      reader.readAsText(file);
    }
  }, [type, isFile, file]);

  return (
    <Box
      className={uploadClasses.uploadSinglePreview.concat(className ? ` ${className}` : '')}
      sx={{
        p: 1,
        top: 0,
        left: 0,
        width: 'auto', // Ensure width is auto
        height: 'auto', // Ensure height is auto
        position: 'relative', // Change to relative to allow positioning
        overflow: 'visible', // Ensure overflow is visible
        ...sx,
      }}
      {...other}
    >
      {type === 'image' && (
        <Box
          component="img"
          alt={fileName}
          src={previewUrl}
          sx={{
            width: '100%', // Ensure image takes full width
            height: 'auto', // Maintain aspect ratio
            borderRadius: 1,
            objectFit: 'cover',
          }}
        />
      )}
      {type === 'video' && (
        <video
          controls
          style={{ width: '100%', height: 'auto' }}
          onClick={(e) => e.preventDefault()}
        >
          <track kind="captions" />
          <source src={previewUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      {type === 'text' && (
        <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="eva:file-text-outline" width={16} sx={{ color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {fileName}
              </Typography>
              {isFile && (
                <Typography variant="caption" color="text.secondary">
                  ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Stack>

            <Box
              sx={{
                p: 1.5,
                bgcolor: 'background.neutral',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                maxHeight: '150px',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {isLoadingText ? (
                <Typography color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                  Loading content...
                </Typography>
              ) : textContent ? (
                <>
                  {textContent.split('\n').slice(0, 15).join('\n')}
                  {textContent.split('\n').length > 15 && (
                    <Typography
                      color="text.secondary"
                      sx={{ mt: 0.5, fontFamily: 'body1.fontFamily', fontSize: '0.7rem' }}
                    >
                      ... and {textContent.split('\n').length - 15} more lines
                    </Typography>
                  )}
                </>
              ) : (
                <Typography color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                  No content to preview
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}
      {type === 'unknown' && (
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 120,
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.neutral',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Iconify icon="eva:file-outline" width={48} sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ textAlign: 'center', wordBreak: 'break-all' }}>
            {fileName}
          </Typography>
          {isFile && (
            <Typography variant="caption" color="text.secondary">
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

export function DeleteButton({ sx, ...other }: IconButtonProps) {
  return (
    <IconButton
      size="small"
      sx={{
        top: 16,
        right: 16,
        zIndex: 9,
        position: 'absolute',
        color: (theme) => varAlpha(theme.vars.palette.common.whiteChannel, 0.8),
        bgcolor: (theme) => varAlpha(theme.vars.palette.grey['900Channel'], 0.72),
        '&:hover': { bgcolor: (theme) => varAlpha(theme.vars.palette.grey['900Channel'], 0.48) },
        ...sx,
      }}
      {...other}
    >
      <Iconify icon="mingcute:close-line" width={18} />
    </IconButton>
  );
}
